import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/contexts/StoreContext';
import type { RectangleElement } from '@/types';
import { handDrawnRoundedRectangle, generateSeed } from '@/utils/handDrawn';
import { getStrokeDashArray, getStrokeLineCap } from '@/utils/strokeStyles';
import { getFillValue } from '@/utils/fillPatterns';

interface RectangleProps {
  element: RectangleElement;
  isSelected: boolean;
}

export const Rectangle: React.FC<RectangleProps> = observer(({ element, isSelected }) => {
  const { uiStore, projectStore } = useStores();
  const { x, y, width, height, style, id, text } = element;
  const { fontSize, textAlign, textVerticalAlign } = style;
  const isEditing = uiStore.editingTextId === element.id;
  
  const [editValue, setEditValue] = useState(text || '');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Generate hand-drawn path with rounded edges
  const seed = generateSeed(id);
  const handDrawnPath = handDrawnRoundedRectangle(x, y, width, height, 8, {
    roughness: 1.5,
    bowing: 0.8,
    seed
  });

  // Hand-drawn font family
  const handDrawnFontFamily = 'Caveat, Kalam, "Comic Sans MS", "Marker Felt", "Bradley Hand", cursive';

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);



  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    const newValue = e.target.value;
    setEditValue(newValue);
    projectStore.updateElement({
      id: element.id,
      updates: { text: newValue }
    });
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    uiStore.stopEditingText();
    uiStore.setActiveTool('select');
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    
    if (e.key === 'Escape') {
      setEditValue(text || '');
      projectStore.updateElement({
        id: element.id,
        updates: { text: text || '' }
      });
      uiStore.stopEditingText();
      uiStore.setActiveTool('select');
    } else if (e.key === 'Tab') {
      // Insert 2 spaces instead of tab character
      e.preventDefault();
      const textarea = e.target as HTMLTextAreaElement;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = editValue.substring(0, start) + '  ' + editValue.substring(end);
      setEditValue(newValue);
      projectStore.updateElement({
        id: element.id,
        updates: { text: newValue }
      });
      
      // Move cursor after the spaces
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
  };



  return (
    <>
      <g>
        {/* Fill */}
        {style.fillColor !== 'transparent' && (
          <path
            d={handDrawnPath}
            fill={getFillValue(style.fillColor, style.fillPattern, `${style.fillPattern}-${style.fillColor.replace('#', '')}`)}
            opacity={style.opacity * 0.8}
          />
        )}
        
        {/* Stroke */}
        <path
          d={handDrawnPath}
          fill="none"
          stroke={style.strokeColor}
          strokeWidth={style.strokeWidth}
          strokeDasharray={getStrokeDashArray(style.strokeStyle, style.strokeWidth)}
          opacity={style.opacity}
          strokeLinecap={getStrokeLineCap(style.strokeStyle)}
          strokeLinejoin="round"
        />

        {/* Invisible clickable area for text editing */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="transparent"
          style={{ cursor: 'text' }}
        />

        {/* Text content with clipping */}
        {text && !isEditing && (
          <g clipPath={`url(#clip-${id})`}>
            <defs>
              <clipPath id={`clip-${id}`}>
                <rect x={x + 4} y={y + 4} width={width - 8} height={height - 8} />
              </clipPath>
            </defs>
            <foreignObject
              x={x + 8}
              y={y + 8}
              width={width - 16}
              height={height - 16}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  color: style.strokeColor === 'transparent' ? '#000000' : style.strokeColor,
                  fontSize: `${fontSize}px`,
                  fontFamily: handDrawnFontFamily,
                  textAlign: textAlign,
                  display: 'flex',
                  alignItems: textVerticalAlign === 'top' ? 'flex-start' : textVerticalAlign === 'bottom' ? 'flex-end' : 'center',
                  justifyContent: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
                  opacity: style.opacity,
                  fontWeight: 'normal',
                  letterSpacing: '0.5px',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  hyphens: 'auto',
                  lineHeight: '1.2',
                  overflow: 'hidden',
                  pointerEvents: 'none',
                  filter: 'url(#roughPaper)',
                  cursor: 'text',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {text}
              </div>
            </foreignObject>
          </g>
        )}

        {isSelected && (
          <g>
            {/* Selection border */}
            <rect
              x={x - 2}
              y={y - 2}
              width={width + 4}
              height={height + 4}
              fill="none"
              stroke="#007acc"
              strokeWidth={2}
              strokeDasharray="5,5"
            />
            {/* Resize handles */}
            {/* Top-left */}
            <rect x={x - 6} y={y - 6} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
            {/* Top-right */}
            <rect x={x + width - 2} y={y - 6} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
            {/* Bottom-left */}
            <rect x={x - 6} y={y + height - 2} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
            {/* Bottom-right */}
            <rect x={x + width - 2} y={y + height - 2} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
            {/* Top-center */}
            <rect x={x + width/2 - 4} y={y - 6} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
            {/* Bottom-center */}
            <rect x={x + width/2 - 4} y={y + height - 2} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
            {/* Left-center */}
            <rect x={x - 6} y={y + height/2 - 4} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
            {/* Right-center */}
            <rect x={x + width - 2} y={y + height/2 - 4} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          </g>
        )}
      </g>
      
      {/* Text editing input */}
      {isEditing && (
        <foreignObject
          x={x + 4}
          y={y + 4}
          width={width - 8}
          height={height - 8}
        >
          <textarea
            ref={inputRef}
            value={editValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            onKeyUp={(e) => e.stopPropagation()}
            onKeyPress={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: '4px',
              padding: '4px',
              fontSize: `${fontSize}px`,
              fontFamily: handDrawnFontFamily,
              textAlign: textAlign,
              background: 'transparent',
              color: style.strokeColor === 'transparent' ? '#000000' : style.strokeColor,
              outline: '1px solid rgba(0, 122, 204, 0.3)',
              fontWeight: 'normal',
              letterSpacing: '0.5px',
              resize: 'none',
              overflow: 'hidden',
              lineHeight: '1.2',
              whiteSpace: 'pre-wrap',
            }}
          />
        </foreignObject>
      )}
    </>
  );
}); 